import { redactText } from "@fabric-harness/sdk";
import { databricksErrorMessage } from "./sdk-error.js";

export const DATABRICKS_AUTHORING_CERTIFICATION_CHECKS = [
  "approval-provenance",
  "jobs-authoring",
  "lakeflow-authoring",
  "ai-search-admin",
  "serving-admin",
  "uc-admin-obo-execution",
  "workspace-write",
  "secrets-write",
  "genie-authoring",
] as const;

/**
 * Tier R checks prove the supported Databricks consumption path. A release certification cannot
 * pass when one of these checks fails or is not configured.
 */
export const DATABRICKS_RELEASE_CERTIFICATION_CHECKS = [
  "identity",
  "ai-gateway",
  "sql",
  "unity-catalog",
  "uc-denial",
  "uc-allowed-select",
  "catalog-preflight-denial",
  "mutation-approval",
  "model-serving",
  "rag",
  "genie",
  "managed-mcp",
  "volumes",
  "lakebase",
  "lakebase-app-restart",
  "dynamic-agent",
  "lineage",
  "obo",
  "system-tables",
  "actual-cost-reconciliation",
  "databricks-app",
] as const;

/** Tier O checks are preview, SKU-specific, or not part of the default product spine. */
export const DATABRICKS_OPTIONAL_CERTIFICATION_CHECKS = [
  "agent-services",
  "rag-evaluation",
  "responses-agent",
  "ai-search",
  "genie-agent-mode",
  "feature-serving",
  "lakeflow",
  "jobs",
  "notebook",
  "jobs-classic-authoring",
  "ai-search-delta-sync-admin",
  "serving-provisioned-throughput-admin",
] as const;

export type DatabricksCertificationTier = "R" | "A" | "O";
export type DatabricksCertificationBlockingTier = Exclude<DatabricksCertificationTier, "O">;

/** Return the default certification tier for a built-in check. Custom checks default to Tier O. */
export function databricksCertificationTier(checkId: string): DatabricksCertificationTier {
  if ((DATABRICKS_RELEASE_CERTIFICATION_CHECKS as readonly string[]).includes(checkId)) return "R";
  if ((DATABRICKS_AUTHORING_CERTIFICATION_CHECKS as readonly string[]).includes(checkId))
    return "A";
  return "O";
}

/** Required checks used by the protected Databricks certification workflow. */
export function defaultDatabricksCertificationChecks(
  options: {
    agentServices?: boolean;
    /** Include protected authoring lifecycles only in a workspace configured for destructive tests. */
    authoring?: boolean;
  } = {},
): string[] {
  const checks: string[] = [...DATABRICKS_RELEASE_CERTIFICATION_CHECKS];
  if (options.authoring) checks.push(...DATABRICKS_AUTHORING_CERTIFICATION_CHECKS);
  if (options.agentServices) checks.splice(checks.indexOf("uc-denial"), 0, "agent-services");
  return checks;
}

export type DatabricksCertificationStatus = "passed" | "failed" | "not-configured";

export interface DatabricksCertificationCheck {
  id: string;
  description: string;
  /** Defaults to the built-in taxonomy for known check ids and Tier O for custom checks. */
  tier?: DatabricksCertificationTier;
  configured: boolean;
  run: () => unknown | Promise<unknown>;
}

export interface DatabricksCertificationResult {
  id: string;
  description: string;
  /** Emitted by current runners; optional so retained schema-v2 evidence remains readable. */
  tier?: DatabricksCertificationTier;
  required: boolean;
  status: DatabricksCertificationStatus;
  durationMs: number;
  error?: string;
  details?: Record<string, unknown>;
}

export interface DatabricksCertificationEvidence {
  schemaVersion: 2;
  evidenceId: string;
  startedAt: string;
  generatedAt: string;
  durationMs: number;
  workspaceHost: string;
  workspaceId?: string;
  commitSha?: string;
  workspaceCloud?: string;
  workspaceRegion?: string;
  runtimeVersion?: string;
  packageVersion?: string;
  principalKind?: string;
  authMode?: "pat" | "oauth-m2m" | "app-service-principal" | "on-behalf-of" | string;
  apiVersions: Record<string, string>;
  requiredChecks: string[];
  optionalChecks: string[];
  /** SHA-256 of the exact npm package tarball exercised by this certification run. */
  packageArtifactSha256?: string;
  /** SHA-256 of the immutable Databricks App build directory deployed by the run. */
  artifactSha256?: string;
  /** Digests of recovery, isolation, and other supporting evidence bound to this manifest. */
  relatedEvidenceSha256?: Record<string, string>;
  passed: boolean;
  summary: { passed: number; failed: number; notConfigured: number };
  results: DatabricksCertificationResult[];
}

export interface RunDatabricksCertificationOptions {
  workspaceHost: string;
  checks: DatabricksCertificationCheck[];
  required?: string[];
  /** Require every check in these tiers. Explicit required ids can only add to this set. */
  requiredTiers?: DatabricksCertificationBlockingTier[];
  commitSha?: string;
  secrets?: string[];
  workspaceCloud?: string;
  workspaceRegion?: string;
  workspaceId?: string;
  runtimeVersion?: string;
  packageVersion?: string;
  principalKind?: string;
  authMode?: DatabricksCertificationEvidence["authMode"];
  apiVersions?: Record<string, string>;
  packageArtifactSha256?: string;
  artifactSha256?: string;
  relatedEvidenceSha256?: Record<string, string>;
}

export interface DatabricksCleanupResource {
  kind: string;
  id: string;
  cleanup: () => void | Promise<void>;
}

export interface DatabricksAuthoringLifecycle<Resource, Mutation = Resource> {
  kind: string;
  create: () => Resource | Promise<Resource>;
  identify: (resource: Resource) => string;
  verifyCreated: (resource: Resource) => unknown | Promise<unknown>;
  mutate: (resource: Resource) => Mutation | Promise<Mutation>;
  verifyMutated: (resource: Resource, mutation: Mutation) => unknown | Promise<unknown>;
  delete: (resource: Resource) => void | Promise<void>;
  verifyDeleted: (resource: Resource) => unknown | Promise<unknown>;
}

export interface DatabricksAuthoringLifecycleEvidence {
  kind: string;
  resourceId: string;
  phases: readonly ["create", "verify", "mutate", "verify-mutation", "delete", "verify-delete"];
  cleanupRequired: boolean;
}

/** Reverse-order cleanup ledger for live lifecycle certification. */
export class DatabricksCertificationCleanupLedger {
  private readonly resources: DatabricksCleanupResource[] = [];
  add(resource: DatabricksCleanupResource): void {
    this.resources.push(resource);
  }
  remove(kind: string, id: string): void {
    const index = this.resources.findIndex(
      (resource) => resource.kind === kind && resource.id === id,
    );
    if (index >= 0) this.resources.splice(index, 1);
  }
  pending(): ReadonlyArray<{ kind: string; id: string }> {
    return this.resources.map(({ kind, id }) => ({ kind, id }));
  }
  async cleanup(): Promise<void> {
    const leaked: Array<{ kind: string; id: string; error: string }> = [];
    for (const resource of [...this.resources].reverse()) {
      try {
        await resource.cleanup();
      } catch (error) {
        leaked.push({
          kind: resource.kind,
          id: resource.id,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
    if (leaked.length)
      throw new Error(
        `Databricks certification cleanup leaked resources: ${JSON.stringify(leaked)}`,
      );
    this.resources.length = 0;
  }
}

/**
 * Exercise a destructive authoring capability with mandatory verification and
 * reverse-order cleanup. Callers should perform each operation through the
 * same governed ToolDef path used in production.
 */
export async function runDatabricksAuthoringLifecycle<Resource, Mutation = Resource>(
  lifecycle: DatabricksAuthoringLifecycle<Resource, Mutation>,
  ledger = new DatabricksCertificationCleanupLedger(),
): Promise<DatabricksAuthoringLifecycleEvidence> {
  const resource = await lifecycle.create();
  const resourceId = lifecycle.identify(resource);
  if (!resourceId.trim()) {
    throw new Error(`Databricks ${lifecycle.kind} lifecycle create returned an empty resource id.`);
  }
  ledger.add({
    kind: lifecycle.kind,
    id: resourceId,
    cleanup: () => lifecycle.delete(resource),
  });

  try {
    await lifecycle.verifyCreated(resource);
    const mutation = await lifecycle.mutate(resource);
    await lifecycle.verifyMutated(resource, mutation);
    await lifecycle.delete(resource);
    await lifecycle.verifyDeleted(resource);
    ledger.remove(lifecycle.kind, resourceId);
    return {
      kind: lifecycle.kind,
      resourceId,
      phases: ["create", "verify", "mutate", "verify-mutation", "delete", "verify-delete"],
      cleanupRequired: false,
    };
  } catch (error) {
    try {
      await ledger.cleanup();
    } catch (cleanupError) {
      throw new AggregateError(
        [error, cleanupError],
        `Databricks ${lifecycle.kind} lifecycle failed and cleanup leaked resources: ${errorMessage(error)}; cleanup: ${errorMessage(cleanupError)}`,
      );
    }
    throw error;
  }
}

function errorMessage(error: unknown): string {
  return databricksErrorMessage(error);
}

/** Run live capability checks and produce stable, secret-redacted CI evidence. */
export async function runDatabricksCertification(
  options: RunDatabricksCertificationOptions,
): Promise<DatabricksCertificationEvidence> {
  const certificationStarted = Date.now();
  const startedAt = new Date(certificationStarted).toISOString();
  const known = new Set(options.checks.map((check) => check.id));
  const explicitlyRequired = new Set(options.required ?? []);
  const unknown = [...explicitlyRequired].filter((id) => !known.has(id));
  if (unknown.length > 0) {
    throw new Error(`Unknown required Databricks certification checks: ${unknown.join(", ")}`);
  }
  const requiredTiers = new Set<DatabricksCertificationTier>(options.requiredTiers ?? []);
  const required = new Set([
    ...explicitlyRequired,
    ...options.checks
      .filter((check) => requiredTiers.has(check.tier ?? databricksCertificationTier(check.id)))
      .map((check) => check.id),
  ]);

  // Required checks commonly carry short-lived OBO credentials or probe an on-demand App.
  // Execute them before optional, potentially long-running evaluation jobs so optional work cannot
  // consume the authority or availability window needed to produce blocking release evidence.
  // Keep the emitted result order aligned with the caller's declaration order for stable evidence.
  const resultsByIndex: Array<DatabricksCertificationResult | undefined> = new Array(
    options.checks.length,
  );
  const executionOrder = options.checks
    .map((check, index) => ({ check, index }))
    .sort(
      (left, right) =>
        Number(required.has(right.check.id)) - Number(required.has(left.check.id)),
    );
  for (const { check, index } of executionOrder) {
    const startedAt = Date.now();
    const requiredCheck = required.has(check.id);
    const tier = check.tier ?? databricksCertificationTier(check.id);
    if (!check.configured) {
      resultsByIndex[index] = {
        id: check.id,
        description: check.description,
        tier,
        required: requiredCheck,
        status: "not-configured",
        durationMs: 0,
        ...(requiredCheck ? { error: "Required check is not configured." } : {}),
      };
      continue;
    }
    try {
      const details = await check.run();
      resultsByIndex[index] = {
        id: check.id,
        description: check.description,
        tier,
        required: requiredCheck,
        status: "passed",
        durationMs: Date.now() - startedAt,
        ...(isRecord(details) ? { details } : {}),
      };
    } catch (error) {
      resultsByIndex[index] = {
        id: check.id,
        description: check.description,
        tier,
        required: requiredCheck,
        status: "failed",
        durationMs: Date.now() - startedAt,
        error: redactText(databricksErrorMessage(error), {
          additionalSecrets: options.secrets ?? [],
        }),
      };
    }
  }
  const results = resultsByIndex.filter(
    (result): result is DatabricksCertificationResult => result !== undefined,
  );

  const summary = {
    passed: results.filter((result) => result.status === "passed").length,
    failed: results.filter((result) => result.status === "failed").length,
    notConfigured: results.filter((result) => result.status === "not-configured").length,
  };
  const passed = results.every((result) => !result.required || result.status === "passed");
  const certifiedPrincipalKind =
    options.principalKind ??
    results.find((result) => result.id === "identity")?.details?.principalKind;
  const generatedAt = new Date().toISOString();
  const environmentIdentity = [
    sanitizeHost(options.workspaceHost),
    options.workspaceId ?? "",
    options.workspaceCloud ?? "",
    options.workspaceRegion ?? "",
    options.authMode ?? "",
    options.packageVersion ?? "",
    options.runtimeVersion ?? "",
    options.packageArtifactSha256 ?? "",
    options.artifactSha256 ?? "",
    JSON.stringify(
      Object.entries(options.relatedEvidenceSha256 ?? {}).sort(([left], [right]) =>
        left.localeCompare(right),
      ),
    ),
    generatedAt,
  ].join("\n");
  const evidenceId = `dbx-cert-${await sha256(environmentIdentity)}`;
  return {
    schemaVersion: 2,
    evidenceId,
    startedAt,
    generatedAt,
    durationMs: Date.now() - certificationStarted,
    workspaceHost: sanitizeHost(options.workspaceHost),
    ...(options.workspaceId ? { workspaceId: options.workspaceId } : {}),
    ...(options.commitSha ? { commitSha: options.commitSha } : {}),
    ...(options.workspaceCloud ? { workspaceCloud: options.workspaceCloud } : {}),
    ...(options.workspaceRegion ? { workspaceRegion: options.workspaceRegion } : {}),
    ...(options.runtimeVersion ? { runtimeVersion: options.runtimeVersion } : {}),
    ...(options.packageVersion ? { packageVersion: options.packageVersion } : {}),
    ...(typeof certifiedPrincipalKind === "string"
      ? { principalKind: certifiedPrincipalKind }
      : {}),
    ...(options.authMode ? { authMode: options.authMode } : {}),
    apiVersions: { ...(options.apiVersions ?? {}) },
    requiredChecks: [...required].sort(),
    optionalChecks: options.checks
      .map((check) => check.id)
      .filter((id) => !required.has(id))
      .sort(),
    ...(options.packageArtifactSha256
      ? { packageArtifactSha256: options.packageArtifactSha256 }
      : {}),
    ...(options.artifactSha256 ? { artifactSha256: options.artifactSha256 } : {}),
    ...(options.relatedEvidenceSha256 && Object.keys(options.relatedEvidenceSha256).length > 0
      ? {
          relatedEvidenceSha256: Object.fromEntries(
            Object.entries(options.relatedEvidenceSha256).sort(([left], [right]) =>
              left.localeCompare(right),
            ),
          ),
        }
      : {}),
    passed,
    summary,
    results,
  };
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sanitizeHost(host: string): string {
  try {
    return new URL(host).origin;
  } catch {
    return host.replace(/\/+$/, "");
  }
}
